import { faFloppyDisk, faPen, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Avatar,
  Button,
  IconButton,
  InputLabel,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import LargeCard from '@/components/LargeCard';
import PhoneConsentFields from '@/components/PhoneConsentFields';
import useConfirm from '@/hooks/useConfirm';
import userAtom from '@/store/user.store';

import useUserInfo from './UserInfo.hooks';
import useStyles from './UserInfo.styles';

const UserInfo = () => {
  const [user] = useAtom(userAtom);
  const { classes } = useStyles();
  const { resetMfa, updateProfile } = useUserInfo();
  const [isEditing, setIsEditing] = useState(false);

  const form = useForm({
    values: {
      name: user?.name ?? '',
      phone_number: user?.phone_number ?? '',
      phone_extension: user?.phone_extension ?? '',
      office_phone_number: user?.office_phone_number ?? '',
      office_phone_extension: user?.office_phone_extension ?? '',
      sms_consent: user?.sms_consent ?? false,
      tos_consent: user?.tos_consent ?? false
    }
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = form;

  const { ConfirmationModal: ResetMfaModal, confirm: confirmResetMfa } =
    useConfirm({
      title: 'Reset MFA',
      message: 'Please confirm that you want to reset your MFA.',
      button: 'Confirm'
    });

  const handleResetMfa = async () => {
    const confirmed = await confirmResetMfa();
    if (confirmed) {
      resetMfa.mutate();
    }
  };

  const onSubmit = (data: {
    name: string;
    phone_number: string;
    phone_extension: string;
    office_phone_number: string;
    office_phone_extension: string;
    sms_consent: boolean;
    tos_consent: boolean;
  }) => {
    updateProfile.mutate(
      {
        name: data.name,
        phone_number: data.phone_number || '',
        phone_extension: data.phone_extension || null,
        office_phone_number: data.office_phone_number || null,
        office_phone_extension: data.office_phone_extension || null,
        sms_consent: data.sms_consent,
        tos_consent: data.tos_consent
      },
      { onSuccess: () => setIsEditing(false) }
    );
  };

  return (
    <LargeCard
      title="User Info"
      rightContent={
        isEditing ? (
          <Stack direction="row">
            <IconButton type="submit" onClick={handleSubmit(onSubmit)}>
              <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
            </IconButton>
            <IconButton
              onClick={() => {
                reset();
                setIsEditing(false);
              }}
            >
              <FontAwesomeIcon size="xs" icon={faXmark} />
            </IconButton>
          </Stack>
        ) : (
          <IconButton onClick={() => setIsEditing(true)}>
            <FontAwesomeIcon size="xs" icon={faPen} />
          </IconButton>
        )
      }
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2}>
            <Stack direction="row" sx={{ alignItems: 'center' }} spacing={2}>
              <ListItemAvatar className={classes.messageListItemAvatar}>
                <Avatar src={user?.image_url || ''}>
                  {user?.userInitials}
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={user?.name} secondary={user?.email} />
              <div>
                <Button onClick={handleResetMfa} variant="contained">
                  Reset MFA
                </Button>
                <ResetMfaModal />
              </div>
            </Stack>
            {isEditing ? (
              <Stack spacing={2} sx={{ maxWidth: 'max(20rem, 40vw)' }}>
                <Stack>
                  <InputLabel>Full Name</InputLabel>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        placeholder="Full name"
                        autoComplete="off"
                        fullWidth
                      />
                    )}
                  />
                </Stack>
                <PhoneConsentFields control={control} errors={errors} />
              </Stack>
            ) : (
              <Stack direction="row" spacing={4}>
                <Stack>
                  <Typography color="text.secondary" variant="body2">
                    Mobile
                  </Typography>
                  <Typography>{user?.phone_number || '——'}</Typography>
                </Stack>
                <Stack>
                  <Typography color="text.secondary" variant="body2">
                    Office
                  </Typography>
                  <Typography>{user?.office_phone_number || '——'}</Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
        </form>
      </FormProvider>
    </LargeCard>
  );
};

export default UserInfo;
